/** @file Sets or clears an animation's length in frames (cleared = it ends at its last key). */
import { z } from 'zod';
import type { AnimationDoc } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import { animationDraft } from './animationDraft';

type Framed = { f: number };
type Tracks = Record<string, Record<string, Framed[] | undefined>>;

/** Number of keys of every timeline of `anim` that lie after frame `end`. */
function keysAfter(anim: AnimationDoc, end: number): number {
  const groups: Tracks[] = [anim.bones, anim.slots, anim.ik];
  const channelKeys = groups
    .flatMap((g) => Object.values(g))
    .flatMap((track) => Object.values(track))
    .filter((keys): keys is Framed[] => keys !== undefined);
  const lists: Framed[][] = [anim.drawOrder, anim.events, ...Object.values(anim.deform), ...channelKeys];
  return lists.reduce((n, keys) => n + keys.filter((k) => k.f > end).length, 0);
}

/** animation.setDuration: keys after the new end are kept and reported (INFO `after`); the same length is a no-op. */
export const command = defineCommand({
  name: 'animation.setDuration',
  description: 'Set the length of an animation in frames, or null to end at its last key; keys after the end stay and are reported.',
  input: z.object({ animation: z.string().min(1), frames: z.number().int().positive().nullable() }).strict(),
  apply(draft, input, ctx) {
    const { anim } = animationDraft(draft, input.animation, ctx);
    if (anim.durationFrames === (input.frames ?? undefined)) {
      ctx.warnings.push({ code: 'INFO', message: 'the animation already has this length; nothing changed', path: 'frames' });
      return;
    }
    draft.meta.updatedAt = ctx.now;
    if (input.frames === null) {
      Reflect.deleteProperty(anim, 'durationFrames');
      return;
    }
    anim.durationFrames = input.frames;
    const after = keysAfter(anim, input.frames);
    if (after > 0) ctx.warnings.push({ code: 'INFO', message: `${after} keys lie after frame ${input.frames}`, params: { after }, path: 'frames' });
  },
});
